import { useEffect, useState } from 'preact/hooks'

import { api } from '../api.js'
import { t } from '../i18n/t.js'
import { melden, schliessen } from '../meldung.js'
import { alarmAn, aus } from '../ton.js'
import { onEvent } from '../ws.js'

/**
 * Ein SOS-Alarm — auf **jedem** Bildschirm, mit Ton.
 *
 * ## Der Befund
 *
 * Der einzige Zuhörer für `sos.*` sass in `routes/Sos.tsx`. Das Bauteil wird
 * nur gerendert, wenn man gerade auf dem SOS-Bildschirm steht — und dort steht
 * niemand, wenn nichts los ist. Löste jemand aus, während man in einem Chat
 * war, auf der Familienseite oder in den Einstellungen, passierte auf dem
 * eigenen Gerät **nichts**: kein Ton, kein Banner, keine Auskunft.
 *
 * Der Server verschickt zwar zusätzlich eine Push-Nachricht. Die weckt aber
 * ein Gerät, dessen App **geschlossen** ist; bei einer offenen App auf einem
 * anderen Bildschirm greift sie nicht. Genau die Lücke dazwischen war offen —
 * bei der Funktion, für die diese App gebaut wurde.
 *
 * Dieselbe Fehlerklasse wie beim eingehenden Anruf zwei Tage zuvor, nur an der
 * Stelle, an der sie am meisten kostet.
 *
 * ## Was hier passiert
 *
 * Drei Wege gleichzeitig, weil einer davon immer ausfallen kann:
 *
 *  1. **Ein Banner**, rot, ganz oben, nicht wegklickbar.
 *  2. **Ein Alarmton** — zwei wechselnde Töne, deutlich anders als ein
 *     Klingeln, dazu ein hartes Vibrationsmuster. Er hört nicht von selbst
 *     auf.
 *  3. **Eine Systemmeldung**, immer, nicht nur bei verdecktem Bildschirm. Bei
 *     einem Anruf wäre das Doppelung; hier ist Doppelung das kleinere Übel.
 *
 * Der Ton endet, wenn sich jemand kümmert (`sos.acknowledged`), der Alarm
 * aufgelöst oder als Fehlalarm zurückgenommen wird — oder wenn der Empfänger
 * den Bildschirm öffnet. Nicht früher.
 */

interface Alarm {
  sosId: string
  personId: string
  test: boolean
}

export function SosAlarm({ meId }: { meId: string }) {
  const [alarm, setAlarm] = useState<Alarm | null>(null)
  const [name, setName] = useState<string | null>(null)

  useEffect(() => onEvent((e) => {
    if (e.type === 'sos.triggered' || e.type === 'sos.escalated') {
      // Der eigene Alarm schlägt beim Auslöser nicht an — der weiss Bescheid,
      // er hat gerade den Knopf gedrückt.
      if (e.ref?.personId === meId) return
      setAlarm({
        sosId: String(e.ref?.sosId),
        personId: String(e.ref?.personId),
        test: e.ref?.kind === 'test',
      })
      return
    }
    if (['sos.acknowledged', 'sos.resolved', 'sos.false_alarm'].includes(e.type)) {
      setAlarm((a) => (a && a.sosId === e.ref?.sosId ? null : a))
    }
  }), [meId])

  useEffect(() => {
    if (!alarm) return
    void api.get<Array<{ id: string; displayName: string }>>('/family/members')
      .then((m) => setName(m.find((p) => p.id === alarm.personId)?.displayName ?? null))
      .catch(() => undefined)
  }, [alarm?.personId])

  /*
   * Ton und Meldung hängen am Alarm, nicht an der Anzeige: Das Bauteil baut
   * sich neu auf, sobald der Name nachgeladen ist, und der Ton finge dann von
   * vorn an.
   */
  useEffect(() => {
    if (!alarm) return
    alarmAn()
    melden({
      titel: alarm.test ? t('🧪 Test-Alarm') : t('🚨 SOS-Alarm'),
      text: t('Jemand aus deiner Familie braucht Hilfe.'),
      marke: `sos-${alarm.sosId}`,
      bleibt: true,
    })
    return () => { aus(); schliessen() }
  }, [alarm?.sosId])

  if (!alarm) return null

  const wer = name ?? t('Jemand')
  return (
    <div class="fh-banner fh-banner--sos" role="alert">
      <span aria-hidden="true">{alarm.test ? '🧪' : '🚨'}</span>
      <div class="fh-banner__text">
        <strong>
          {alarm.test
            ? t('{wer} probt einen Alarm', { wer })
            : t('{wer} braucht Hilfe', { wer })}
        </strong>
      </div>
      {/*
        Der Knopf beendet den Ton und geht hin. Das Kümmern selbst passiert auf
        dem SOS-Bildschirm — hier wäre es ein zweiter Ort, an dem eine Zusage
        richtig sein muss.
      */}
      <button
        class="fh-btn fh-btn--gefahr fh-banner__action"
        onClick={() => { aus(); schliessen(); setAlarm(null); location.hash = '#sos' }}
      >
        {t('Ansehen')}
      </button>
    </div>
  )
}
